// CreateNewPortfolioModal.tsx — “Select a portfolio” then shared creation form (no intermediate type picker)
// import { TrashIcon } from '@heroicons/react/24/outline'
import { PlusIcon } from '@heroicons/react/24/solid'
import Tooltip from '@mui/material/Tooltip'
import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import type { CSSProperties } from 'react'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
// import { createPortal } from 'react-dom'
import Modal from 'react-modal'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import {
  cancel_button,
  EDITOR_SEARCH_DEBOUNCE_MS,
  EDITOR_SEARCH_LIMIT,
  EDITOR_SEARCH_MIN_LENGTH,
  popup_padding,
  save_button,
} from 'src/config/config'
import { PORTFOLIO_VISIBILITY_PILL } from 'src/constants/constants'
import {
  ISharedPortfolioMembershipItemFragment,
  ISharedPortfolioSolutionItemFragment,
  useCreateSharedPortfolioWithMembersMutation,
  useSearchPortfolioEditorsQuery,
} from 'src/graphql/generated/hooks'
import { useDebouncedValue } from 'src/hooks/useDebouncedValue'
import { showToast } from 'src/store/actions/toastAction'
import type { PortfolioEditorSuggestion } from 'src/types/portfolioEditorSuggestion'

export const CREATE_NEW_PORTFOLIO_ID = '__create_new__' as const

export type PortfolioPickerOption =
  | {
      kind: 'create-new'
      id: typeof CREATE_NEW_PORTFOLIO_ID
      title: string
      subtitle: string
    }
  | {
      kind: 'portfolio'
      id: string
      title: string
      subtitle: string
      visibility: 'private' | 'shared'
      slug?: string
      shared_portfolio_memberships?: ISharedPortfolioMembershipItemFragment[]
      shared_portfolio_solutions?: ISharedPortfolioSolutionItemFragment[]
    }

export type { PortfolioEditorSuggestion }

interface CreateNewPortfolioModalProps {
  isOpen: boolean
  onClose: () => void
  portfolioOptions: PortfolioPickerOption[]
  /** When user adds to an existing portfolio (not create-new). */
  onAddSolution?: (portfolioId: string) => void | Promise<void>
  /** Runs after shared portfolio is created successfully (e.g. refetch lists). */
  onSubmitSharedPortfolio?: (payload: { name: string; editors: PortfolioEditorSuggestion[] }) => void | Promise<void>
}

const modalStyles: { content: CSSProperties; overlay: CSSProperties } = {
  content: {
    top: '48%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    padding: '0px',
    transform: 'translate(-50%, -50%)',
    borderWidth: '0px',
    backgroundColor: 'transparent',
    border: '0px solid transparent',
    // maxHeight: '83vh',
    overflow: 'visible',
  },
  overlay: { zIndex: 1300 },
}

const FIGMA = {
  listWidthPx: 1100,
  rowHeightPx: 132,
  /** Article / caption (Figma “article copy 13”) */
  articleCopy13Px: 14,
  articleCopyLineHeightPx: 18,
  /** Portfolio item title (Figma: Poppins 700, 18px, line-height 100%) */
  listingTitlePx: 18,
  /** Heading row: title + more control */
  headingRowMinHeightPx: 27,
  /** Figma measured width of title + actions row inside 800px frame */
  headingRowMaxWidthPx: '100%', // 671.286,
  rowPaddingX: 24,
  /** Icon container — Figma desktop 80×80 */
  iconBoxPx: 80,
  /** User / users glyph inside tile (Figma: 40×40) */
  iconGlyphPx: 40,
  /** Gap after icon so title starts at ~110px from row left (24 + 80 + 6) */
  iconToTextGapPx: 30,
  /** Private / Shared chip — match MyPortfoliosList */
  visibilityPillMinWidthPx: 70,
  visibilityPillMinHeightPx: 30,
  visibilityPillPaddingY: 8,
  visibilityPillPaddingX: 14,
  visibilityPillRadiusPx: 15,
  privateVisibilityPillBg: PORTFOLIO_VISIBILITY_PILL.privateBg,
  privateVisibilityPillText: PORTFOLIO_VISIBILITY_PILL.privateText,
  sharedVisibilityPillBg: PORTFOLIO_VISIBILITY_PILL.sharedBg,
  sharedVisibilityPillText: PORTFOLIO_VISIBILITY_PILL.sharedText,
  /** Neutral chip (e.g. editor role label) */
  visibilityPillBg: '#F1F3F5',
  /** Gap between heading row and pill (Figma: 8px) */
  titleToPillGapPx: 5,
  borderSubtle: '#E6E6E6',
  /** Unfollow row — native checkbox layout (Figma) */
  unfollowCheckboxSizePx: 10.5,
  unfollowCheckboxInsetPx: 1.75,
  /** Inset + size + inset */
  unfollowCheckboxFramePx: 14,
  unfollowCheckboxAccent: '#011D58',
} as const

/** Figma Frame 39 “Add Name” / “Add Editors”: Poppins Bold 700, 18px, line-height 100%, h 27px. */
const sharedFormSectionHeading =
  'poppins-font flex h-[27px] flex-none items-center text-[18px] font-bold leading-none tracking-normal text-darkgray opacity-100'

/** Matches PremiumFollowSolution / Hub modal list rows (Poppins 18 semibold + Karla 16 body, #3329ef selection).
 * Phone: wrap. md+: ellipsis. Pill stays vertically centered with the title block. */
const portfolioCardRowTitle =
  'poppins-font block min-w-0 whitespace-normal break-words text-[18px] font-semibold leading-[22px] text-topicDescription md:truncate'
const portfolioCardRowSubtitle = 'karla-font block text-[16px] font-normal leading-[22px] text-topicDescription'

/** Title with ellipsis; MUI tooltip only when text is actually truncated. */
export const PortfolioOptionTitle: React.FC<{ title: string; className?: string }> = ({ title, className }) => {
  const titleRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const measureTruncation = () => {
    const element = titleRef.current
    if (!element) return
    setIsTruncated(element.scrollWidth > element.clientWidth)
  }

  useLayoutEffect(() => {
    measureTruncation()
  }, [title])

  useLayoutEffect(() => {
    const element = titleRef.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measureTruncation())
    observer.observe(element)
    return () => observer.disconnect()
  }, [title])

  const content = (
    <span ref={titleRef} className={className}>
      {title}
    </span>
  )

  if (!title || !isTruncated) {
    return content
  }

  return (
    <Tooltip
      title={title}
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: '#616161',
            color: '#FFFFFF',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '12px',
            marginBottom: '6px !important',
          },
        },
      }}
    >
      {content}
    </Tooltip>
  )
}

const EDITOR_SEARCH_FORM_SCROLL_PX = 70

const SHARED_PORTFOLIO_NAME_MIN_LENGTH = 3
const SHARED_PORTFOLIO_NAME_MAX_LENGTH = 50
// const SHARED_PORTFOLIO_NAME_PATTERN = /^[a-zA-Z0-9\s]+$/
const SHARED_PORTFOLIO_NAME_ERROR_MESSAGE =
  'Portfolio name must contain 3–50 characters and cannot include special characters.'

function getSharedPortfolioNameValidationError(name: string): string | null {
  const trimmed = name.trim()
  if (trimmed.length < SHARED_PORTFOLIO_NAME_MIN_LENGTH || trimmed.length > SHARED_PORTFOLIO_NAME_MAX_LENGTH) {
    return SHARED_PORTFOLIO_NAME_ERROR_MESSAGE
  }
  /* if (!SHARED_PORTFOLIO_NAME_PATTERN.test(trimmed)) {
    return SHARED_PORTFOLIO_NAME_ERROR_MESSAGE
  } */
  return null
}

export const CreateNewPortfolioModal: React.FC<CreateNewPortfolioModalProps> = ({
  isOpen,
  onClose,
  portfolioOptions,
  onAddSolution,
  onSubmitSharedPortfolio,
}) => {
  const dispatch = useDispatch()
  const [selectedId, setSelectedId] = useState<string>(CREATE_NEW_PORTFOLIO_ID)
  const [step, setStep] = useState<'pick' | 'shared-details'>('shared-details')
  const [sharedPortfolioName, setSharedPortfolioName] = useState('')
  const [editorQuery, setEditorQuery] = useState('')
  const trimmedEditorQuery = editorQuery.trim()
  const debouncedEditorKeyword = useDebouncedValue(trimmedEditorQuery, EDITOR_SEARCH_DEBOUNCE_MS)
  const editorSearchKeyword = debouncedEditorKeyword.length >= EDITOR_SEARCH_MIN_LENGTH ? debouncedEditorKeyword : ''
  const { data: editorsSearchData, loading: editorsSearchLoading } = useSearchPortfolioEditorsQuery({
    variables: { keyword: editorSearchKeyword || undefined, limit: EDITOR_SEARCH_LIMIT },
    skip: !isOpen || step !== 'shared-details' || !editorSearchKeyword,
  })
  const editorSuggestions = useMemo(() => {
    const users = editorsSearchData?.searchSharedPortfolioOrganizationUsers?.users ?? []
    return users.map((u) => {
      const email = u.email
      const name = (u.name?.trim() || email.split('@')[0] || 'Member') as string
      return { id: String(u.id), name, email }
    })
  }, [editorsSearchData])
  const isEditorSearchPending =
    trimmedEditorQuery.length >= EDITOR_SEARCH_MIN_LENGTH && trimmedEditorQuery !== debouncedEditorKeyword
  const loadingSuggestions = Boolean(isEditorSearchPending || (editorsSearchLoading && editorSearchKeyword))

  const [createSharedPortfolioWithMembers, { loading: creatingSharedPortfolio }] =
    useCreateSharedPortfolioWithMembersMutation()
  const [editorPickerOpen, setEditorPickerOpen] = useState(false)
  const [selectedEditors, setSelectedEditors] = useState<PortfolioEditorSuggestion[]>([])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [updatingMemberRoleId, setUpdatingMemberRoleId] = useState<string | null>(null)
  const editorFieldRef = useRef<HTMLDivElement | null>(null)
  const formContainerRef = useRef<HTMLDivElement | null>(null)
  // const editorInputRef = useRef<HTMLInputElement | null>(null)
  // const editorSuggestionsRef = useRef<HTMLUListElement | null>(null)
  // const [editorSuggestionsPos, setEditorSuggestionsPos] = useState<{
  //   top: number
  //   left: number
  //   width: number
  //   maxHeight: number
  // } | null>(null)

  const options = useMemo(() => portfolioOptions, [portfolioOptions])

  const filteredEditorSuggestions = useMemo(() => {
    if (trimmedEditorQuery.length < EDITOR_SEARCH_MIN_LENGTH) return []
    if (trimmedEditorQuery !== debouncedEditorKeyword) return []
    const selectedIds = new Set(selectedEditors.map((e) => e.id))
    return editorSuggestions.filter((person) => !selectedIds.has(person.id))
  }, [trimmedEditorQuery, debouncedEditorKeyword, editorSuggestions, selectedEditors])

  const showEditorSuggestions =
    editorPickerOpen &&
    trimmedEditorQuery.length >= EDITOR_SEARCH_MIN_LENGTH &&
    (loadingSuggestions || filteredEditorSuggestions.length > 0)
  const showSearchingLi = showEditorSuggestions && loadingSuggestions && filteredEditorSuggestions.length === 0

  useLayoutEffect(() => {
    if (!showSearchingLi) return
    const container = formContainerRef.current
    if (!container) return
    const maxScroll = container.scrollHeight - container.clientHeight
    container.scrollTop = Math.min(container.scrollTop + EDITOR_SEARCH_FORM_SCROLL_PX, maxScroll)
  }, [showSearchingLi, editorSearchKeyword])

  useEffect(() => {
    if (isOpen) {
      setSelectedId(CREATE_NEW_PORTFOLIO_ID)
      setStep('shared-details')
      setSharedPortfolioName('')
      setEditorQuery('')
      setEditorPickerOpen(false)
      setSelectedEditors([])
    }
  }, [isOpen])

  // const updateEditorSuggestionsPos = useCallback(() => {
  //   const input = editorInputRef.current
  //   if (!input) return
  //   const rect = input.getBoundingClientRect()
  //   const gap = 4
  //   const viewportPadding = 8
  //   const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding
  //   setEditorSuggestionsPos({
  //     top: rect.bottom + gap,
  //     left: rect.left,
  //     width: rect.width,
  //     maxHeight: Math.max(120, spaceBelow),
  //   })
  // }, [])

  // const showEditorSuggestions =
  //   editorPickerOpen && editorQuery.trim() && (loadingSuggestions || filteredEditorSuggestions.length > 0)
  // )

  // useLayoutEffect(() => {
  //   if (!showEditorSuggestions) {
  //     setEditorSuggestionsPos(null)
  //     return
  //   }
  //   updateEditorSuggestionsPos()
  //   window.addEventListener('resize', updateEditorSuggestionsPos)
  //   window.addEventListener('scroll', updateEditorSuggestionsPos, true)
  //   return () => {
  //     window.removeEventListener('resize', updateEditorSuggestionsPos)
  //     window.removeEventListener('scroll', updateEditorSuggestionsPos, true)
  //   }
  // }, [showEditorSuggestions, updateEditorSuggestionsPos, filteredEditorSuggestions.length, loadingSuggestions])

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!editorFieldRef.current?.contains(e.target as Node)) {
        setEditorPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [])

  const addDisabled = selectedId === CREATE_NEW_PORTFOLIO_ID || !onAddSolution

  const resetSharedForm = () => {
    setSharedPortfolioName('')
    setEditorQuery('')
    setEditorPickerOpen(false)
    setSelectedEditors([])
  }

  const handleClose = () => {
    onClose?.()

    setTimeout(() => {
      setSelectedId(CREATE_NEW_PORTFOLIO_ID)
      // setStep('pick')
      resetSharedForm()
    }, 300)
  }

  const backToPortfolioPicker = () => {
    // setStep('pick')
    // resetSharedForm()
    handleClose()
  }

  const handleModalRequestClose = () => {
    if (step === 'shared-details') {
      //backToPortfolioPicker()
      handleClose()
      return
    }
  }

  const closeAriaLabel = step === 'shared-details' ? 'Back to portfolio selection' : 'Close'

  const handleAddSolution = async () => {
    if (addDisabled) return
    await onAddSolution?.(selectedId)
    handleClose()
  }

  const handleAddEditor = (person: PortfolioEditorSuggestion) => {
    if (selectedEditors.some((e) => e.id === person.id)) return
    person.role = 'editor'
    setSelectedEditors((prev) => [...prev, person])
    setEditorQuery('')
    setEditorPickerOpen(false)
  }

  const handleRemoveEditor = (id: string) => {
    setSelectedEditors((prev) => prev.filter((e) => e.id !== id))
  }

  const trimmedSharedPortfolioName = sharedPortfolioName.trim()

  const handleSubmitSharedPortfolio = async () => {
    const name = trimmedSharedPortfolioName
    if (name.length < SHARED_PORTFOLIO_NAME_MIN_LENGTH || creatingSharedPortfolio) return

    if (name.length > SHARED_PORTFOLIO_NAME_MAX_LENGTH) {
      dispatch(showToast('Shared portfolio name cannot exceed 50 characters.', 'info'))
      return
    }

    const nameValidationError = getSharedPortfolioNameValidationError(name)
    if (nameValidationError) {
      dispatch(showToast(nameValidationError, 'info', { autoClose: 4000 }))
      return
    }

    const members = selectedEditors.map((ed) => ({ email: ed.email, name: ed.name, role: ed.role || 'editor' }))
    try {
      const result = await createSharedPortfolioWithMembers({
        variables: {
          name,
          members: members.length > 0 ? members : undefined,
        },
      })
      if (result.errors?.length) {
        toast.error(result.errors.map((e) => e.message).join(' ') || 'Could not create portfolio.', {
          theme: 'colored',
          autoClose: 4000,
        })
        return
      }
      handleClose()

      await onSubmitSharedPortfolio?.({ name, editors: selectedEditors })
      dispatch(showToast(`Shared portfolio “${name}” has been created successfully.`, 'success'))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not create portfolio.'
      // toast.error(message, { theme: 'colored', autoClose: 4000 })
      dispatch(showToast(message, 'info', { autoClose: 4000 }))
    }
  }

  function isMembershipAdminRole(role: string | null | undefined): boolean {
    const r = (role ?? '').trim().toLowerCase()
    return r === 'admin' || r === 'owner'
  }

  const handleToggleRole = async (id: string) => {
    // if (!sharedPortfolioId.trim() || updatingMemberRoleId) return
    const target = selectedEditors.find((e) => e.id === id)
    if (!target) return

    const previousRole = isMembershipAdminRole(target.role) ? 'admin' : 'editor'
    const nextRole = previousRole === 'admin' ? 'editor' : 'admin'

    setSelectedEditors((prev) => prev.map((e) => (e.id === id ? { ...e, role: nextRole } : e)))
    setUpdatingMemberRoleId(id)
    try {
      // const result = await updateSharedPortfolioMemberRole({
      //   variables: {
      //     sharedPortfolioId,
      //     memberUserId: id,
      //     role: nextRole,
      //   },
      // })
      // if (result.errors?.length) {
      //   throw new Error(result.errors.map((e) => e.message).join(' ') || 'Could not update member role.')
      // }
      // dispatch(
      //   showToast(`${target.name} is now ${nextRole === 'admin' ? 'Admin' : 'Editor'}.`, 'success', {
      //     autoClose: 2500,
      //   }),
      // )
    } catch (err) {
      setSelectedEditors((prev) => prev.map((e) => (e.id === id ? { ...e, role: previousRole } : e)))
      // const message = err instanceof Error ? err.message : 'Could not update member role.'
      // dispatch(showToast(message, 'error', { autoClose: 4000 }))
    } finally {
      setUpdatingMemberRoleId((prev) => (prev === id ? null : prev))
    }
  }

  const sharedSubmitDisabled =
    trimmedSharedPortfolioName.length < SHARED_PORTFOLIO_NAME_MIN_LENGTH || creatingSharedPortfolio

  const selectedEditorNameStyle =
    'karla-font box-border block min-h-[22px] min-w-0 w-full  text-[16px] font-medium leading-[22px] tracking-normal text-lthblue opacity-100 hover:cursor-default'
  const selectedEditorEmailStyle =
    'karla-font box-border block min-w-0 w-full break-all line-clamp-2 text-[16px] font-normal leading-[22px] tracking-normal text-lthblue opacity-100 hover:cursor-default'

  return (
    <Modal
      closeTimeoutMS={200}
      isOpen={isOpen}
      onRequestClose={handleModalRequestClose}
      style={modalStyles}
      ariaHideApp={false}
    >
      <div className="portfolio-modal-container relative flex max-h-[83vh] w-[calc(100vw-2rem)] md:w-[600px] flex-col mt-0 md-[10px] md:my-[20px] my-[10px] mx-[10px]">
        <span
          className="ml-auto top-[26px] right-[-12px] cursor-pointer text-textdark relative md:top-[24px] md:right-[-10px]"
          onClick={handleModalRequestClose}
          aria-label={closeAriaLabel}
        >
          <div className="w-4 h-4 m-2 rounded-md bg-white absolute z-20"></div>
          <IonIcon name="close-circle" className="!h-[30px] !w-[30px] relative z-30" />
        </span>

        <div
          className={`premium-follow-solution-container relative mx-auto flex max-h-[76vh] min-h-0 w-full flex-col overflow-hidden rounded-lg bg-white p-6 shadow-lg ${popup_padding} md:w-[600px]`}
        >
          {/* Center of control sits on the modal’s top-right corner (~¼ on panel, rest past edge) */}

          {step === 'pick' && (
            <>
              <div className="mb-4 shrink-0 border-b border-mist pb-[16px]">
                <h4 className="poppins-font min-w-0 text-[24px] font-bold leading-[24px] text-darkgray">
                  Select a portfolio
                </h4>
              </div>
              <p className="karla-font mb-[24px] shrink-0 text-[18px] font-normal leading-[22px] text-darkgray">
                Add this solution to your portfolio, or to a shared portfolio.
              </p>

              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain [-webkit-overflow-scrolling:touch]">
                <ul
                  className="mb-[16px] flex w-full flex-col gap-[8px] bg-white pb-2"
                  role="radiogroup"
                  aria-label="Portfolio options"
                >
                  {options.map((opt) => {
                    const isSelected = selectedId === opt.id
                    const isCreate = opt.kind === 'create-new'

                    return (
                      <li key={opt.id} className="w-full list-none">
                        <button
                          type="button"
                          role={isCreate ? undefined : 'radio'}
                          aria-checked={isCreate ? undefined : isSelected}
                          onClick={() => {
                            if (isCreate) {
                              setStep('shared-details')
                              return
                            }
                            setSelectedId(opt.id)
                          }}
                          className={cn(
                            'flex min-h-[86px] w-full max-w-[512px] flex-row items-center gap-6 rounded-lg border border-solid px-4 py-4 text-left transition-colors',
                            isCreate
                              ? 'border-[#c5cbd3] bg-[#D6DADF] hover:bg-[#C8CDD4]'
                              : isSelected
                                ? 'border-[#3329ef] bg-[#f1f0fe]'
                                : 'border-[#dddddd] bg-white hover:bg-gray-50/80',
                          )}
                        >
                          {isCreate ? (
                            <span
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-textdark [&>svg]:block [&>svg]:shrink-0"
                              aria-hidden
                            >
                              <PlusIcon className="h-6 w-6 shrink-0 text-[#011D58]" aria-hidden />
                            </span>
                          ) : (
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center" aria-hidden>
                              <span
                                className={cn(
                                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                                  isSelected ? 'border-[#3329ef]' : 'border-[#dddddd]',
                                )}
                              >
                                {isSelected ? <span className="h-2.5 w-2.5 rounded-full bg-[#3329ef]" /> : null}
                              </span>
                            </span>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="mb-[7px] block min-w-0">
                              <PortfolioOptionTitle title={opt.title} className={portfolioCardRowTitle} />
                            </span>
                            <span className={portfolioCardRowSubtitle}>{opt.subtitle}</span>
                          </span>
                          {opt.kind === 'portfolio' ? (
                            <span
                              className="box-border inline-flex h-[30px] w-max shrink-0 items-center justify-center whitespace-nowrap font-primary font-medium tracking-normal opacity-100"
                              style={{
                                minWidth: FIGMA.visibilityPillMinWidthPx,
                                minHeight: FIGMA.visibilityPillMinHeightPx,
                                paddingTop: FIGMA.visibilityPillPaddingY,
                                paddingRight: FIGMA.visibilityPillPaddingX,
                                paddingBottom: FIGMA.visibilityPillPaddingY,
                                paddingLeft: FIGMA.visibilityPillPaddingX,
                                borderRadius: FIGMA.visibilityPillRadiusPx,
                                backgroundColor:
                                  opt.visibility === 'private'
                                    ? FIGMA.privateVisibilityPillBg
                                    : FIGMA.sharedVisibilityPillBg,
                                color:
                                  opt.visibility === 'private'
                                    ? FIGMA.privateVisibilityPillText
                                    : FIGMA.sharedVisibilityPillText,
                                fontSize: FIGMA.articleCopy13Px,
                                lineHeight: `${FIGMA.visibilityPillMinHeightPx - 2 * FIGMA.visibilityPillPaddingY}px`,
                                letterSpacing: 0,
                              }}
                            >
                              {opt.visibility === 'private' ? 'Private' : 'Shared'}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>

              <div className="relative flex shrink-0 flex-row flex-wrap md:gap-[16px] gap-[8px]">
                <button
                  type="button"
                  onClick={() => void handleAddSolution()}
                  disabled={addDisabled}
                  className={cn(
                    save_button,
                    addDisabled ? 'cursor-not-allowed bg-[#b3bbcd] text-white' : 'cursor-pointer bg-navy text-white',
                  )}
                >
                  <p className="text-white">Add Solution</p>
                </button>

                <button className={`${cancel_button}`} type="button" onClick={handleClose}>
                  <p className="text-[#333333]">Cancel</p>
                </button>
              </div>
            </>
          )}

          {step === 'shared-details' && (
            <>
              <div className="mb-4 shrink-0 border-b border-mist pb-[16px]">
                <h4 className="poppins-font min-w-0 text-[24px] font-bold leading-[24px] text-darkgray">
                  Create a new shared portfolio
                </h4>
              </div>

              <div
                ref={formContainerRef}
                className="edit-shared-portfolio-form-container min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain [-webkit-overflow-scrolling:touch]"
              >
                <div className="flex flex-col gap-[15px] pb-0 pr-2">
                  <p className="karla-font shrink-0 text-[18px] font-normal leading-[22px] text-darkgray">
                    Name your new portfolio and share it with teammates.
                  </p>

                  <div>
                    <p className={`${sharedFormSectionHeading} w-[98px]`}>Add Name</p>
                    <div className="w-full py-1">
                      <label
                        htmlFor="shared-portfolio-name"
                        className="poppins-font mb-2 mt-2 flex min-h-[21px] w-[512px] max-w-full flex-none items-center text-[14px] font-medium leading-none tracking-normal text-[#333333] opacity-100"
                      >
                        Shared Portfolio Name
                      </label>
                      <input
                        id="shared-portfolio-name"
                        type="text"
                        value={sharedPortfolioName}
                        onChange={(e) => {
                          const nextValue = e.target.value
                          if (nextValue.length > SHARED_PORTFOLIO_NAME_MAX_LENGTH) {
                            dispatch(showToast('Shared portfolio name cannot exceed 50 characters.', 'info'))
                            return
                          }
                          setSharedPortfolioName(nextValue)
                        }}
                        placeholder=""
                        className="karla-font box-border h-[40px] min-h-[40px] max-h-[40px] w-[512px] max-w-full rounded-[3px] border border-solid border-[#767676] bg-white px-4 py-[11px] align-middle text-[15px] font-medium leading-none tracking-normal text-[#011D58] opacity-100 outline-none ring-0 placeholder:text-[#011D58] focus:ring-0 focus-visible:ring-0"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="">
                    <p className={`${sharedFormSectionHeading} w-full mb-[8px] text-[#333333]`}>Add Editors</p>
                    <p className="karla-font mb-[8px]  box-border block min-h-[44px] w-[512px] max-w-full text-[18px] font-normal leading-[22px] tracking-normal text-[#333333] opacity-100">
                      Editors can add and remove items. Only the admin (you) can add or remove editors.
                    </p>
                    <label className="poppins-font mb-[5px] flex min-h-[21px] w-[512px] max-w-full flex-none items-center text-[14px] font-medium leading-none tracking-normal text-[#333333] opacity-100">
                      Portfolio Editors
                    </label>

                    <div ref={editorFieldRef} className="relative z-50 max-w-[512px]">
                      <input
                        id="portfolio-editor-search"
                        aria-label="Search for portfolio editors"
                        type="text"
                        value={editorQuery}
                        onChange={(e) => {
                          setEditorQuery(e.target.value)
                          setEditorPickerOpen(true)
                        }}
                        onFocus={() => setEditorPickerOpen(true)}
                        placeholder="Search by name or email"
                        className="karla-font box-border h-[40px] min-h-[40px] max-h-[40px] w-[512px] max-w-full rounded-[3px] border border-solid border-[#767676] bg-white px-4 py-[11px] align-middle text-[15px] font-medium leading-none tracking-normal text-[#011D58] opacity-100 outline-none ring-0 placeholder:text-[#767676] focus:ring-0 focus-visible:ring-0"
                        autoComplete="off"
                        aria-expanded={editorPickerOpen}
                        aria-controls="portfolio-editor-suggestions"
                        aria-autocomplete="list"
                      />
                      {showEditorSuggestions ? (
                        <ul
                          id="portfolio-editor-suggestions"
                          role="listbox"
                          className=" min-h-[70px] selected-editors-container absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(240px,40vh)] overflow-y-auto rounded-lg border border-solid border-[#dddddd] bg-white py-1 shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
                        >
                          {loadingSuggestions && filteredEditorSuggestions.length === 0 ? (
                            <li className="karla-font px-4 py-3 text-[14px] text-darkgray">Searching…</li>
                          ) : null}
                          {filteredEditorSuggestions.map((person) => (
                            <li key={person.id} role="presentation">
                              <button
                                type="button"
                                role="option"
                                aria-selected={false}
                                onClick={() => handleAddEditor(person)}
                                className="flex w-full cursor-pointer flex-row flex-nowrap items-center px-4 py-3 text-left transition-colors hover:bg-[#f1f0fe]"
                              >
                                <span className="flex min-h-[22px] w-full min-w-0 flex-col md:flex-row items-start md:items-center gap-x-2 gap-y-0.5">
                                  <span
                                    title={person.name}
                                    aria-label={person.name}
                                    aria-describedby={person.name}
                                    className={selectedEditorNameStyle}
                                  >
                                    {person.name}
                                  </span>
                                  <span
                                    className={selectedEditorEmailStyle}
                                    title={person.email}
                                    aria-label={person.email}
                                    aria-describedby={person.email}
                                  >
                                    {person.email}
                                  </span>
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>

                    {selectedEditors.length > 0 ? (
                      <ul className="mt-4 flex max-w-[512px] flex-col gap-3">
                        {selectedEditors.map((ed) => {
                          const isAdmin = isMembershipAdminRole(ed.role || 'editor')
                          return (
                            <li key={ed.id}>
                              <div className="flex items-center justify-between gap-4 rounded-lg border border-solid border-[#dddddd] bg-white px-4 py-3">
                                <div className="min-w-0 flex-1">
                                  <span className="flex min-h-[22px] w-full min-w-0 flex-col flex-nowrap items-start gap-x-2 overflow-hidden">
                                    <span
                                      className={selectedEditorNameStyle}
                                      title={ed.name}
                                      aria-label={ed.name}
                                      aria-describedby={ed.name}
                                    >
                                      {ed.name}
                                    </span>
                                    <span
                                      title={ed.email}
                                      aria-label={ed.email}
                                      aria-describedby={ed.email}
                                      className={selectedEditorEmailStyle}
                                    >
                                      {ed.email}
                                    </span>
                                  </span>
                                </div>

                                <div className="flex shrink-0 items-center gap-4">
                                  <label
                                    htmlFor={`edit-shared-portfolio-editor-role-${ed.id}`}
                                    className="box-border inline-flex w-max cursor-pointer items-center justify-center gap-2 whitespace-nowrap font-primary font-normal tracking-normal text-textdark opacity-100"
                                    style={{
                                      // marginTop: FIGMA.titleToPillGapPx,
                                      minWidth: FIGMA.visibilityPillMinWidthPx,
                                      minHeight: FIGMA.visibilityPillMinHeightPx,
                                      paddingTop: FIGMA.visibilityPillPaddingY,
                                      paddingRight: FIGMA.visibilityPillPaddingX,
                                      paddingBottom: FIGMA.visibilityPillPaddingY,
                                      paddingLeft: FIGMA.visibilityPillPaddingX,
                                      borderRadius: FIGMA.visibilityPillRadiusPx,
                                      backgroundColor: FIGMA.visibilityPillBg,
                                      fontSize: FIGMA.articleCopy13Px,
                                      lineHeight: `${FIGMA.visibilityPillMinHeightPx - 2 * FIGMA.visibilityPillPaddingY}px`,
                                      letterSpacing: 0,
                                      // marginLeft: '10px',
                                    }}
                                    aria-label={`${isAdmin ? 'Make Editor for' : 'Make Admin for'} ${ed.name}`}
                                  >
                                    <input
                                      type="checkbox"
                                      id={`edit-shared-portfolio-editor-role-${ed.id}`}
                                      checked={isAdmin}
                                      // disabled={ updatingMemberRoleId === ed.id}
                                      onChange={() => void handleToggleRole(ed.id)}
                                    />
                                    {isAdmin ? 'Admin' : 'Admin'}
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveEditor(ed.id)}
                                    className="inline-flex shrink-0 items-center justify-center rounded text-darkgray hover:bg-gray-50 hover:text-textdark"
                                    aria-label={`Remove ${ed.name}`}
                                  >
                                    <svg
                                      width="16"
                                      height="18"
                                      viewBox="0 0 16 18"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M3 18C2.45 18 1.97917 17.8042 1.5875 17.4125C1.19583 17.0208 1 16.55 1 16V3H0V1H5V0H11V1H16V3H15V16C15 16.55 14.8042 17.0208 14.4125 17.4125C14.0208 17.8042 13.55 18 13 18H3ZM5 14H7V5H5V14ZM9 14H11V5H9V14Z"
                                        fill="#333333"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="mt-7 flex w-full min-w-0 flex-row flex-wrap items-center md:gap-[16px] gap-[8px]">
                <button
                  type="button"
                  onClick={() => void handleSubmitSharedPortfolio()}
                  disabled={sharedSubmitDisabled}
                  className={cn(
                    save_button,
                    sharedSubmitDisabled
                      ? 'cursor-not-allowed bg-navy text-white'
                      : 'cursor-pointer bg-navy text-white',
                  )}
                >
                  <p className="text-white">
                    {creatingSharedPortfolio ? 'Create Shared Portfolio' : 'Create Shared Portfolio'}
                  </p>
                </button>

                <button className={`${cancel_button}`} type="button" onClick={() => backToPortfolioPicker()}>
                  <p className="text-[#333333]">Cancel</p>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
